/**
 * Hook personalizado para gestionar lógica de productos
 * Maneja: buscar, crear, obtener productos
 */

import { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3002') + '/api';

const getAuthHeaders = (empresaId) => {
  const token = localStorage.getItem('token');
  return {
    'empresa-id': empresaId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const useProductos = (empresaId) => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar productos
  const buscarProductos = useCallback(
    async (query) => {
      if (!query || query.trim().length === 0) {
        setProductos([]);
        return [];
      }

      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/productos/buscar?q=${encodeURIComponent(query)}`,
          { headers: getAuthHeaders(empresaId) }
        );

        const resultados = response.data || [];
        setProductos(resultados);
        return resultados;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        setProductos([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Crear producto
  const crearProducto = useCallback(
    async (productoData) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/productos`,
          productoData,
          {
            headers: {
              'empresa-id': empresaId,
              'Content-Type': 'application/json',
            },
          }
        );

        const nuevoProducto = response.data.data || response.data;
        return nuevoProducto;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Obtener producto por ID
  const obtenerProducto = useCallback(
    async (productoId) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/productos/${productoId}`,
          {
            headers: {
              'empresa-id': empresaId,
            },
          }
        );

        return response.data.data || response.data;
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [empresaId]
  );

  // Limpiar error
  const limpiarError = useCallback(() => {
    setError(null);
  }, []);

  return {
    productos,
    loading,
    error,
    buscarProductos,
    crearProducto,
    obtenerProducto,
    limpiarError,
  };
};
